import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const lancerStatCardVariants = cva(
  "relative grid min-w-[4.5rem] gap-1 rounded-[3px] border p-2 text-center transition-colors",
  {
    variants: {
      tone: {
        default: "border-border/70 bg-card shadow-sm",
        accent: "border-primary/40 bg-primary/5",
        success: "border-success/40 bg-success/5",
        warning: "border-warning/50 bg-warning/5",
        destructive: "border-destructive/50 bg-destructive/5",
        info: "border-info/50 bg-info/5",
        muted: "border-muted/70 bg-muted/30 opacity-80",
      },
      size: {
        sm: "min-w-[3.5rem] p-1.5",
        default: "min-w-[4.5rem] p-2",
        lg: "min-w-[6rem] p-3",
      },
    },
    defaultVariants: {
      tone: "default",
      size: "default",
    },
  },
);

const lancerStatValueVariants = cva("font-mono font-bold leading-none", {
  variants: {
    size: {
      sm: "text-sm",
      default: "text-xl",
      lg: "text-2xl",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const lancerStatLabelVariants = cva(
  "font-mono font-semibold uppercase tracking-wider text-muted-foreground",
  {
    variants: {
      size: {
        sm: "text-[9px] leading-none",
        default: "text-[10px] leading-none",
        lg: "text-xs leading-none",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

type LancerStatCardTone = NonNullable<
  VariantProps<typeof lancerStatCardVariants>["tone"]
>;
type LancerStatCardSize = NonNullable<
  VariantProps<typeof lancerStatCardVariants>["size"]
>;

export interface LancerStatCardProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof lancerStatCardVariants> {
  label: React.ReactNode;
  value: React.ReactNode;
  decoration?: "none" | "notch" | "screws";
}

function LancerStatCard({
  label,
  value,
  tone = "default",
  size = "default",
  decoration = "notch",
  className,
  children,
  ...props
}: LancerStatCardProps) {
  return (
    <div
      data-slot="lancer-stat-card"
      data-tone={tone}
      data-size={size}
      className={cn(lancerStatCardVariants({ tone, size }), className)}
      {...props}
    >
      {decoration === "screws" && (
        <>
          <span className="absolute left-0.5 top-0.5 size-1 rounded-full bg-muted-foreground/30" />
          <span className="absolute right-0.5 top-0.5 size-1 rounded-full bg-muted-foreground/30" />
          <span className="absolute bottom-0.5 left-0.5 size-1 rounded-full bg-muted-foreground/30" />
          <span className="absolute bottom-0.5 right-0.5 size-1 rounded-full bg-muted-foreground/30" />
        </>
      )}
      {decoration === "notch" && (
        <>
          <span className="absolute left-1/2 top-0 h-0.5 w-2 -translate-x-1/2 rounded-b-sm bg-muted-foreground/20" />
          <span className="absolute bottom-0 left-1/2 h-0.5 w-2 -translate-x-1/2 rounded-t-sm bg-muted-foreground/20" />
        </>
      )}
      <div
        data-slot="lancer-stat-card-label"
        className={cn(lancerStatLabelVariants({ size }))}
      >
        {label}
      </div>
      <div
        data-slot="lancer-stat-card-value"
        className={cn(lancerStatValueVariants({ size }))}
      >
        {value}
      </div>
      {children ? (
        <div
          data-slot="lancer-stat-card-extra"
          className="text-[10px] text-muted-foreground"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export type { LancerStatCardSize, LancerStatCardTone };
export {
  LancerStatCard,
  lancerStatCardVariants,
  lancerStatLabelVariants,
  lancerStatValueVariants,
};
